import ClearIcon from "@mui/icons-material/Clear";
import FileIcon from "@mui/icons-material/InsertDriveFile";
import {
  Alert,
  AlertTitle,
  LinearProgress,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { FullScreenDocumentPreview, extensionFromName } from "components/Document/shared";
import { useCheckForFile } from "hooks/useCheckForFile";
import { useCallback, useMemo, useState } from "react";

import { formatScoutFileSecondaryText } from "../../scoutAttachmentLimits";
import { ScoutFilePart } from "./scoutChatMessage";

const UPLOADED_FILE_URL_PREFIX = "encamp-upload://";

function parseUploadStorageKey(url: string | undefined): string | undefined {
  if (!url?.startsWith(UPLOADED_FILE_URL_PREFIX)) {
    return undefined;
  }
  const storageKey = url.slice(UPLOADED_FILE_URL_PREFIX.length);
  return storageKey.length > 0 ? storageKey : undefined;
}

type Props = {
  part: ScoutFilePart;
  onRemove?: () => void;
  state?: "default" | "error";
  errorText?: string;
};

export const ScoutFileDisplay = ({ part, onRemove, state = "default", errorText }: Props) => {
  const theme = useTheme();
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const { fetchFile } = useCheckForFile();

  const uploadStorageKey = useMemo(() => parseUploadStorageKey(part.url), [part.url]);
  const historySignedUrl = part.document?.signedDownloadUrl;
  const canPreview = historySignedUrl !== undefined || uploadStorageKey !== undefined;
  const fileExtension = extensionFromName(part.filename ?? part.title ?? "");

  const resolveSignedPreviewUrl = useCallback(async (): Promise<string | undefined> => {
    if (historySignedUrl) {
      return historySignedUrl;
    }
    if (uploadStorageKey) {
      return (await fetchFile(uploadStorageKey)) ?? undefined;
    }
    return undefined;
  }, [fetchFile, historySignedUrl, uploadStorageKey]);

  const openPreview = useCallback(async () => {
    if (!canPreview || isPreviewLoading) {
      return;
    }
    if (previewUrl) {
      setIsPreviewOpen(true);
      return;
    }
    if (historySignedUrl) {
      setPreviewUrl(historySignedUrl);
      setIsPreviewOpen(true);
      return;
    }
    setIsPreviewLoading(true);
    try {
      const signedUrl = await resolveSignedPreviewUrl();
      if (!signedUrl) {
        return;
      }
      setPreviewUrl(signedUrl);
      setIsPreviewOpen(true);
    } finally {
      setIsPreviewLoading(false);
    }
  }, [canPreview, historySignedUrl, isPreviewLoading, previewUrl, resolveSignedPreviewUrl]);

  const isError = state === "error";
  if (isError) {
    return (
      <Alert
        severity="error"
        sx={{ width: "100%" }}
        action={
          onRemove ? (
            <Tooltip title="Remove invalid file">
              <IconButton
                edge="end"
                size="small"
                aria-label={`remove-file-${part.id}`}
                onClick={onRemove}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          ) : undefined
        }
      >
        <AlertTitle>{`Error: ${part.title ?? part.filename ?? "Attachment"}`}</AlertTitle>
        <Typography variant="body2">{errorText ?? "Upload failed."}</Typography>
      </Alert>
    );
  }

  const secondaryText = formatScoutFileSecondaryText({
    mediaType: part.mediaType ?? "application/octet-stream",
    ingestionMode: part.ingestionMode,
    extractionStatus: part.extractionStatus,
    extractionProgress: part.extractionProgress,
  });

  const showExtractionProgress =
    part.ingestionMode === "LARGE_PDF_EXTRACTION" &&
    (part.extractionStatus === "RUNNING" || part.extractionStatus === "PENDING");
  const pagesTotal = part.extractionProgress?.pagesTotal ?? 0;
  const pagesCompleted = part.extractionProgress?.pagesCompleted ?? 0;
  const progressValue =
    pagesTotal > 0 ? Math.min(100, Math.round((pagesCompleted / pagesTotal) * 100)) : undefined;

  return (
    <>
      <ListItem
        dense
        onClick={canPreview ? () => void openPreview() : undefined}
        sx={{
          bgcolor: theme.palette.encampBlue.light,
          borderRadius: 2,
          cursor: canPreview ? "pointer" : "default",
          opacity: isPreviewLoading ? 0.8 : 1,
        }}
        slotProps={{ secondaryAction: { sx: { top: "24px", right: "8px" } } }}
        secondaryAction={
          <Stack direction="row" spacing={0.5}>
            {onRemove ? (
              <Tooltip title="Remove file">
                <IconButton
                  edge="end"
                  size="small"
                  aria-label={`remove-file-${part.id}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemove();
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        }
      >
        <ListItemIcon sx={{ minWidth: "unset", paddingRight: theme.spacing(2) }}>
          <FileIcon />
        </ListItemIcon>
        <ListItemText
          primary={part.title ?? part.filename ?? "Attachment"}
          secondary={
            <Stack spacing={0.75} sx={{ mt: 0.25 }}>
              <Typography variant="body2" color="text.secondary" component="span">
                {secondaryText}
              </Typography>
              {showExtractionProgress ? (
                <LinearProgress
                  variant={progressValue !== undefined ? "determinate" : "indeterminate"}
                  value={progressValue}
                  sx={{ height: 6, borderRadius: 1 }}
                />
              ) : null}
            </Stack>
          }
          slotProps={{ secondary: { component: "div" } }}
        />
      </ListItem>
      {previewUrl ? (
        <FullScreenDocumentPreview
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          fileUrl={previewUrl}
          fileExtension={fileExtension}
          title={part.title ?? part.filename ?? "Attachment Preview"}
        />
      ) : null}
    </>
  );
};
